import { Status } from '@tcs/common/types';
import { createContext, ReactNode, useState } from 'react';

export interface Notification {
  status: Status;
  text: string;
  action?: ReactNode;
  type: string;
  id?: string;
}

interface ContextProps {
  notifications: Notification[];
  addNotification: (
    notification: Notification,
    shouldShowFirst?: boolean,
    shouldMerge?: boolean
  ) => void;
  addNotifications: (
    newNotifications: Notification[],
    shouldShowFirst?: boolean,
    shouldMerge?: boolean
  ) => void;
  clearNotification: (type: string, id?: string) => void;
  clearNotifications: () => void;
}

interface NotificationProviderProps {
  children: ReactNode;
}

export const NotificationContext = createContext<ContextProps>({
  notifications: [],
  addNotification: () => undefined,
  addNotifications: () => undefined,
  clearNotification: () => undefined,
  clearNotifications: () => undefined,
});

const NotificationProvider = ({ children }: NotificationProviderProps) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);

  const addNotification = (
    notification,
    shouldShowFirst = false,
    shouldMerge = false
  ) => {
    addNotifications([notification], shouldShowFirst, shouldMerge);
  };

  const addNotifications = (
    newNotifications: Notification[],
    shouldShowFirst = false,
    shouldMerge = false
  ) => {
    let result = newNotifications;
    if (shouldMerge) {
      result = shouldShowFirst
        ? [...newNotifications, ...notifications]
        : [...notifications, ...newNotifications];
    }
    setNotifications(result);
  };

  const clearNotifications = () => {
    setNotifications([]);
  };

  const clearNotification = (type: string, id?: string) => {
    if (id) {
      setNotifications(
        notifications.filter(
          (notification) => notification.type !== type && notification.id === id
        )
      );
      return;
    }

    setNotifications(
      notifications.filter((notification) => notification.type !== type)
    );
  };

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        addNotification,
        addNotifications,
        clearNotification,
        clearNotifications,
      }}>
      {children}
    </NotificationContext.Provider>
  );
};

export default NotificationProvider;
